import { Component } from '@theme/component';
import { debounce, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent, SubmenuActiveEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  /**
   * @type {number | null}
   */
  #submenuSyncFrame = null;

  connectedCallback() {
    super.connectedCallback();

    this.removeAttribute('aria-expanded');
    this.addEventListener('pointerenter', this.#stampSubmenus, { once: true });
    this.addEventListener('focusin', this.#stampSubmenus, { once: true });
    this.addEventListener('pointerenter', this.#preloadImages, { once: true });
    this.addEventListener('focusin', this.#preloadImages, { once: true });
    this.#stampSubmenusWhenIdle();
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
    clearTimeout(this.#coldOpenTimer);
    this.#coldOpenTimer = undefined;
    this.#coldOpenItem = null;
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupSubmenuObservers();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#syncActiveSubmenuDimensions();
      });
    });
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Queue a submenu height sync after the browser has had time to apply layout updates.
   */
  #scheduleActiveSubmenuSync() {
    if (this.#submenuSyncFrame) return;

    this.#submenuSyncFrame = requestAnimationFrame(() => {
      this.#submenuSyncFrame = requestAnimationFrame(() => {
        this.#submenuSyncFrame = null;
        this.#syncActiveSubmenuDimensions();
      });
    });
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Delayed close — absorbs spurious pointerleave during sticky/clip-path layout sync.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivateTimer;

  /**
   * Hover-intent delay before opening when the menu is fully closed ("cold open"),
   * so an accidental fly-over of the menu bar doesn't pop a submenu.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #coldOpenTimer;

  /**
   * The menu item a pending cold open is waiting on. Used so hit-test jitter within the
   * same item (e.g. the `::after` hover-bridge overflowing below the item box) doesn't
   * cancel or restart the pending open.
   * @type {HTMLElement | null}
   */
  #coldOpenItem = null;

  /**
   * Delay in ms before a cold open (nothing currently active) is committed.
   * @type {number}
   */
  #coldOpenDelay = 150;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap
      ? this.headerComponent?.querySelector('.header__row--top')
      : this.headerComponent;
    item.style.setProperty(
      '--box-height',
      `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`
    );
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(':hover') ?? false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Measure the currently active submenu after layout changes.
   */
  #syncActiveSubmenuDimensions() {
    const headerComponent = this.headerComponent;
    if (!headerComponent) return;

    if (headerComponent.dataset.menuStyle === 'drawer') {
      this.#deactivate();
      return;
    }

    const activeItem = this.#state.activeItem;
    if (!activeItem) {
      headerComponent.style.setProperty('--submenu-height', '0px');
      this.#setFullOpenHeaderHeight(0);
      return;
    }

    const isDefaultSlot = !activeItem.closest('[slot="overflow"]');
    const { finalHeight } = this.#resolveSubmenuMetrics(activeItem, isDefaultSlot);

    headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
  }

  /**
   * Resolve the submenu element and its effective height.
   * @param {HTMLElement} item
   * @param {boolean} isDefaultSlot
   * @returns {{ submenu: HTMLElement | null, finalHeight: number }}
   */
  #resolveSubmenuMetrics(item, isDefaultSlot) {
    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Ensure the submenu is marked active before measuring height.
    // This switches `content-visibility` from `auto` to `visible`, preventing
    // `offsetHeight` from returning the `contain-intrinsic-size` fallback value.
    // Never apply this to the overflow menu container itself.
    if (submenu && submenu !== this.overflowMenu) {
      submenu.dataset.active = '';
    }

    let finalHeight = submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    return { submenu, finalHeight };
  }

  /**
   * Whether the pointer is still over the open menu item or its submenu.
   * Used to ignore spurious pointerleave during clip-path / sticky layout sync.
   * @returns {boolean}
   */
  #isPointerOverOpenMenu() {
    const activeItem = this.#state.activeItem;
    if (!activeItem) return false;

    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return true;

    const { x, y } = this.#lastPointer;
    const underPointer = document.elementFromPoint(x, y);
    if (!(underPointer instanceof Element)) return false;

    const listItem = activeItem.parentElement;
    const submenu = findSubmenu(activeItem);

    return Boolean(listItem?.contains(underPointer) || submenu?.contains(underPointer));
  }

  /**
   * Handle hover/focus on a menu item. Opens immediately when switching between items
   * within an already-open menu or when focused via keyboard; applies a hover-intent
   * delay only on a "cold open" (menu fully closed) triggered by the pointer, so an
   * accidental fly-over of the menu bar doesn't pop a submenu.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#stampSubmenus();
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;

    if (event instanceof PointerEvent) {
      this.#lastPointer.x = event.clientX;
      this.#lastPointer.y = event.clientY;
    }

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    // Cold open from the pointer: defer opening until the hover intent is confirmed.
    // A pointerleave that genuinely exits the item cancels it (see `deactivate`).
    if (this.#state.activeItem == null && event instanceof PointerEvent) {
      // Already waiting on this same item? Keep the running timer — re-entering after
      // hit-test jitter (the `::after` hover-bridge below the item box) must not restart it.
      if (this.#coldOpenTimer && this.#coldOpenItem === item) return;

      clearTimeout(this.#coldOpenTimer);
      this.#coldOpenItem = item;
      this.#coldOpenTimer = setTimeout(() => {
        this.#coldOpenTimer = undefined;
        this.#coldOpenItem = null;
        // No hit-test guard here: `pointerenter` already proved the pointer entered the
        // item, and a genuine pointerleave before this fires cancels the timer in `deactivate`.
        // A coordinate/`:hover` re-test would falsely reject entry from below (bottom edge
        // and the scroll-container padding strip are not part of the item's hover area).
        this.#open(item);
      }, this.#coldOpenDelay);
      return;
    }

    this.#open(item);
  };

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown = (event) => {
    if (event.key === 'Escape') {
      const activeItem = this.#state.activeItem;
      if (!activeItem) return;

      event.stopPropagation();
      this.#deactivate(activeItem);
      activeItem.focus();
      return;
    }

    const trigger = findMenuItem(event.target);

    if (!(trigger instanceof HTMLElement) || trigger !== document.activeElement) return;
    if (trigger.getAttribute('aria-haspopup') !== 'true') return;

    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'ArrowDown') return;

    event.preventDefault();

    if (this.#state.activeItem === trigger) {
      if (event.key === 'ArrowDown') {
        this.#focusFirstSubmenuLink(trigger);
        return;
      }
      this.#deactivate(trigger);
      return;
    }

    this.#open(trigger);

    if (event.key === 'ArrowDown') this.#focusFirstSubmenuLink(trigger);
  };

  /**
   * @param {HTMLElement} trigger
   */
  #focusFirstSubmenuLink(trigger) {
    const submenu = findSubmenu(trigger);
    const first = submenu?.querySelector('a[href], button:not([disabled])');

    if (first instanceof HTMLElement) first.focus();
  }

  /**
   * Commit activation of the selected menu item immediately.
   * @param {HTMLElement} item
   */
  #open = (item) => {
    if (!this.headerComponent || item == this.#state.activeItem) return;

    // Determine overflow vs default slot from the resolved menu item, not event.target,
    // since event.target can be a child element within the menu item.
    const isDefaultSlot = !item.closest('[slot="overflow"]');

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    item.ariaExpanded = 'true';

    const { submenu, finalHeight } = this.#resolveSubmenuMetrics(item, isDefaultSlot);

    if (submenu) {
      document.dispatchEvent(new SubmenuActiveEvent());

      submenu.inert = false;

      // Keep the overflow menu container out of the active-state path.
      if (submenu !== this.overflowMenu) {
        submenu.dataset.active = '';
      }

      resetSubmenuScroll(submenu);

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupSubmenuObservers();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        this.#scheduleActiveSubmenuSync();
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      this.#submenuResizeObserver = new ResizeObserver(() => {
        this.#scheduleActiveSubmenuSync();
      });
      this.#submenuResizeObserver.observe(submenu);

      const submenuInner = submenu.querySelector(
        '.menu-list__submenu-inner, [part="overflow-list"]'
      );
      if (submenuInner instanceof HTMLElement) {
        this.#submenuResizeObserver.observe(submenuInner);
      }
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    if (event instanceof PointerEvent) {
      this.#lastPointer.x = event.clientX;
      this.#lastPointer.y = event.clientY;
    }

    // Abort a pending cold open only when the pointer genuinely leaves the pending item.
    // A pointerleave whose relatedTarget is still inside that same item is hit-test jitter
    // (the `::after` hover-bridge overflows below the item box) and must not cancel it.
    if (this.#coldOpenTimer) {
      const pendingListItem = this.#coldOpenItem?.parentElement;
      const related = event.relatedTarget;
      const stayingWithinPendingItem =
        related instanceof Node && Boolean(pendingListItem?.contains(related));
      if (!stayingWithinPendingItem) {
        clearTimeout(this.#coldOpenTimer);
        this.#coldOpenTimer = undefined;
        this.#coldOpenItem = null;
      }
    }

    const activeItem = this.#state.activeItem;
    const menu = findSubmenu(activeItem);
    const listItem = activeItem?.parentElement;
    const related = event.relatedTarget;

    const isMovingWithinOpenItem =
      related instanceof Node &&
      Boolean(listItem?.contains(related) || menu?.contains(related));
    const isMovingWithinMenu =
      related instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      related instanceof Node && event.type === 'blur' && Boolean(menu?.contains(related));
    const isMovingToOverflowMenu =
      related instanceof Node && related.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinOpenItem || isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (activeItem && (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu)) {
        this.#stopPointerTracking(activeItem);
      }
      return;
    }

    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = undefined;
      if (this.#isPointerOverOpenMenu()) return;
      this.#deactivate();
    }, 150);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#cleanupSubmenuObservers();
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      submenu.inert = true;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const topRow = /** @type {HTMLElement | null} */ (
      this.headerComponent.querySelector('.header__row--top')
    );
    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? (topRow?.offsetHeight ?? 0)
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  /**
   * Preload images that are set to load lazily.
   */
  #stampSubmenusWhenIdle() {
    const stamp = () => {
      if (this.closest('header-component')?.dataset.menuStyle === 'menu') this.#stampSubmenus();
    };
    const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(stamp, { timeout: 4000 }) : setTimeout(stamp, 1500));

    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  #stampSubmenus = () => {
    const templates = this.querySelectorAll('template[data-submenu-content]');

    if (!templates.length) return;

    for (const template of templates) {
      if (template instanceof HTMLTemplateElement) template.replaceWith(template.content);
    }

    this.querySelector('[data-menu-links-fallback]')?.remove();
    this.updatedCallback();
  };

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]:not(.mega-menu__banner-image)');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupSubmenuObservers() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;

    if (this.#submenuSyncFrame) {
      cancelAnimationFrame(this.#submenuSyncFrame);
      this.#submenuSyncFrame = null;
    }
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Reset the scrollable submenu content so every new hover opens from the top.
 * @param {HTMLElement} submenu
 */
function resetSubmenuScroll(submenu) {
  const scrollContainer = submenu.querySelector(
    '.menu-list__submenu-inner, [part="overflow-list"]'
  );
  if (scrollContainer instanceof HTMLElement) {
    scrollContainer.scrollTop = 0;
  }
}
